import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import styled from "styled-components";
import { useQuery, useMutation } from "@apollo/client";
import { Button as LatitudeButton } from "@flexport/latitude-typescript";
import {
  AdminClientsDocument,
  AdminDeleteClientDocument,
  AdminCreateClientDocument,
  AdminUpdateClientDocument,
} from "../../generated/graphql";
import AdminClientGrid from "./grids/AdminClientGrid";
import ConfirmDialog from "../../components/ConfirmDialog";
import { isSessionExpiredError } from "../../contexts/SessionExpiredContext";

const ClientListContainer = styled.div`
  margin-top: 20px;
`;

const FormOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const FormContainer = styled.div`
  background-color: white;
  padding: 24px;
  border-radius: 4px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const FormTitle = styled.h2`
  margin-top: 0;
  font-size: 20px;
  margin-bottom: 16px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormField = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    border-color: #40a9ff;
    outline: none;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

const Checkbox = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
`;

const CheckboxInput = styled.input`
  margin-right: 8px;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  gap: 12px;
`;

const ErrorMessage = styled.div`
  color: #ff4d4f;
  margin-top: 10px;
  font-size: 14px;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const AddButtonContainer = styled.div`
  margin-bottom: 16px;
`;

export type ClientListRefType = {
  refetchClients: () => void;
};

interface ClientFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    client_name: string;
    folder: string;
    upload_complete?: boolean;
  }) => void;
  initialData?: {
    clientId: string;
    client_name: string;
    folder: string;
    upload_complete: boolean;
  };
  title: string;
  submitLabel: string;
}

const ClientForm: React.FC<ClientFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  title,
  submitLabel,
}) => {
  const [formData, setFormData] = useState({
    client_name: initialData?.client_name || "",
    folder: initialData?.folder || "",
    upload_complete: initialData?.upload_complete || false,
  });
  const [error, setError] = useState("");

  useEffect(() => {
    if (initialData) {
      setFormData({
        client_name: initialData.client_name,
        folder: initialData.folder,
        upload_complete: initialData.upload_complete,
      });
    } else {
      setFormData({
        client_name: "",
        folder: "",
        upload_complete: false,
      });
    }
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!formData.client_name.trim()) {
      setError("Client name is required");
      return;
    }

    if (!formData.folder.trim()) {
      setError("Folder is required");
      return;
    }

    onSubmit(formData);
  };

  return (
    <FormOverlay onClick={onClose} data-testid="client-form-overlay">
      <FormContainer
        onClick={(e) => e.stopPropagation()}
        data-testid="client-form-container"
      >
        <FormTitle data-testid="client-form-title">{title}</FormTitle>
        <Form onSubmit={handleSubmit} data-testid="client-form">
          <FormField>
            <Label htmlFor="client_name">Client Name</Label>
            <Input
              id="client_name"
              name="client_name"
              value={formData.client_name}
              onChange={handleChange}
              placeholder="Enter client name"
              data-testid="client-name-input"
            />
          </FormField>
          <FormField>
            <Label htmlFor="folder">Folder</Label>
            <Input
              id="folder"
              name="folder"
              value={formData.folder}
              onChange={handleChange}
              placeholder="Enter folder path"
              data-testid="client-folder-input"
            />
          </FormField>
          {initialData && (
            <FormField>
              <Checkbox>
                <CheckboxInput
                  type="checkbox"
                  id="upload_complete"
                  name="upload_complete"
                  checked={formData.upload_complete}
                  onChange={handleChange}
                  data-testid="client-upload-complete-checkbox"
                />
                <Label htmlFor="upload_complete" style={{ margin: 0 }}>
                  Upload Complete
                </Label>
              </Checkbox>
            </FormField>
          )}
          {error && (
            <ErrorMessage data-testid="client-form-error">{error}</ErrorMessage>
          )}
          <ButtonGroup>
            <LatitudeButton
              label="Cancel"
              intent="basic"
              kind="hollow"
              size="m"
              isLoading={false}
              width="responsive"
              type="button"
              disabled={false}
              onClick={onClose}
              data-testid="client-form-cancel"
            >
              Cancel
            </LatitudeButton>
            <LatitudeButton
              label={submitLabel}
              intent="basic"
              kind="solid"
              size="m"
              isLoading={false}
              width="responsive"
              type="submit"
              disabled={false}
              data-testid="client-form-submit"
            >
              {submitLabel}
            </LatitudeButton>
          </ButtonGroup>
        </Form>
      </FormContainer>
    </FormOverlay>
  );
};

const AdminClientList = forwardRef<ClientListRefType, {}>((_, ref) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<any>(null);

  // State for custom delete confirmation dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<{
    id: string;
    name: string;
    hasFiles: boolean;
  } | null>(null);

  const { loading, data, refetch } = useQuery(AdminClientsDocument);

  useImperativeHandle(ref, () => ({
    refetchClients: () => refetch(),
  }));

  const [createClient] = useMutation(AdminCreateClientDocument, {
    onCompleted: () => {
      refetch();
      setIsCreateModalOpen(false);
    },
  });

  const [updateClient] = useMutation(AdminUpdateClientDocument, {
    onCompleted: () => {
      refetch();
      setIsEditModalOpen(false);
    },
  });

  const [deleteClient] = useMutation(AdminDeleteClientDocument, {
    onCompleted: () => {
      refetch();
    },
  });

  const handleCreateClient = (data: {
    client_name: string;
    folder: string;
  }) => {
    createClient({
      variables: {
        client_name: data.client_name,
        folder: data.folder,
      },
    });
  };

  const handleUpdateClient = (data: {
    client_name: string;
    folder: string;
    upload_complete?: boolean;
  }) => {
    if (!selectedClient) return;

    updateClient({
      variables: {
        clientId: selectedClient.clientId,
        client_name: data.client_name,
        folder: data.folder,
        upload_complete: data.upload_complete,
      },
    });
  };

  // Get clients data
  const clients = data?.admin?.clients || [];

  // Handle edit button click - opens edit modal
  const handleEdit = (client: any) => {
    setSelectedClient(client);
    setIsEditModalOpen(true);
  };

  // Handle delete button click - shows confirmation dialog
  const handleDelete = (clientId: string) => {
    const client = clients.find((c: any) => c.clientId === clientId);
    if (!client) return;

    const hasFiles = (client.files?.length || 0) > 0;
    setClientToDelete({
      id: clientId,
      name: client.client_name,
      hasFiles,
    });
    setShowDeleteDialog(true);
  };

  // Confirm delete after dialog confirmation
  const confirmDelete = async () => {
    if (!clientToDelete || clientToDelete.hasFiles) {
      setShowDeleteDialog(false);
      setClientToDelete(null);
      return;
    }

    try {
      await deleteClient({
        variables: { clientId: clientToDelete.id },
      });
      setShowDeleteDialog(false);
      setClientToDelete(null);
    } catch (error) {
      console.error("Error deleting client:", error);
      // Skip alert when session-expired modal will handle the error
      if (!isSessionExpiredError()) {
        alert("Failed to delete client. Please try again.");
      }
      setShowDeleteDialog(false);
      setClientToDelete(null);
    }
  };

  if (loading) {
    return <LoaderContainer>Loading clients...</LoaderContainer>;
  }

  return (
    <ClientListContainer>
      <AddButtonContainer>
        <LatitudeButton
          label="Add New Client"
          intent="basic"
          kind="hollow"
          size="m"
          isLoading={false}
          width="responsive"
          type="button"
          disabled={false}
          onClick={() => setIsCreateModalOpen(true)}
          data-testid="create-client-button"
        >
          Add New Client
        </LatitudeButton>
      </AddButtonContainer>

      {clients.length > 0 ? (
        <AdminClientGrid
          rows={clients}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      ) : (
        <p>No clients found.</p>
      )}

      {/* Custom delete confirmation dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="File Uploader"
        message={
          clientToDelete?.hasFiles
            ? `Cannot delete client "${clientToDelete?.name}" because it has files.\n\nPlease delete all files first.`
            : `Are you sure you want to permanently delete client "${clientToDelete?.name}"?\n\nThis action cannot be undone.`
        }
        confirmText={clientToDelete?.hasFiles ? "OK" : "Delete"}
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteDialog(false);
          setClientToDelete(null);
        }}
      />

      <ClientForm
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateClient}
        title="Add New Client"
        submitLabel="Create"
      />

      <ClientForm
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleUpdateClient}
        initialData={selectedClient}
        title="Edit Client"
        submitLabel="Update"
      />
    </ClientListContainer>
  );
});

export default AdminClientList;
